import { ResponsiveBar } from "@nivo/bar";
import { format, startOfMonth } from "date-fns";
import { TrendingUp } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { memo, useMemo } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { ListErrorState } from "../design/ListStates";
import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { es } from "date-fns/locale";
import { APP_LOCALE } from "../misc/locale";

// Almikia design-system.md section 4: bars use navy and navy tints only
// (never the semantic green/red or the brand gradient), so "won", "pending"
// and "lost" read as one navy family, darkest to lightest.
const NAVY = "#272c57";
const NAVY_70 = "#686b89";
const NAVY_40 = "#a9abbc";
/** Text style shared by every axis/legend label: navy tint + tabular numbers. */
const tabularTickStyle = (fill: string) => ({
  fill,
  fontVariantNumeric: "tabular-nums",
});

const WON_STAGE = "venta_cerrada";
const LOST_STAGE = "cerrado_perdido";

// ASSUMPTION (Almikia Epic E2, TASK-011): probability weights for the 5 kanban
// states are placeholders until the E7 dashboard epic revisits them. Any
// stage not listed here (legacy Atomic values included) falls back to
// DEFAULT_PENDING_MULTIPLIER instead of producing NaN.
const multiplier: Record<string, number> = {
  contacto_inicial: 0.2,
  discovery: 0.4,
  showing: 0.6,
  cierre: 0.8,
  administracion: 0.9,
};
const DEFAULT_PENDING_MULTIPLIER = 0.3;

const threeMonthsAgo = new Date(
  new Date().setMonth(new Date().getMonth() - 6),
).toISOString();

export const DealsChart = memo(() => {
  const translate = useTranslate();
  const { dealStages, currency } = useConfigurationContext();
  const wonLabel = findDealLabel(dealStages, WON_STAGE) ?? "Won";
  const lostLabel = findDealLabel(dealStages, LOST_STAGE) ?? "Lost";

  const { data, isPending, error, refetch } = useGetList<Deal>("deals", {
    pagination: { perPage: 100, page: 1 },
    sort: {
      field: "created_at",
      order: "ASC",
    },
    filter: {
      "created_at@gte": threeMonthsAgo,
      "is_test_data@neq": true,
    },
  });
  const months = useMemo(() => {
    if (!data) return [];
    const dealsByMonth = data.reduce((acc, deal) => {
      const month = startOfMonth(deal.created_at ?? new Date()).toISOString();
      if (!acc[month]) {
        acc[month] = [];
      }
      acc[month].push(deal);
      return acc;
    }, {} as any);

    const amountByMonth = Object.keys(dealsByMonth).map((month) => {
      return {
        date: format(month, "MMM", { locale: es }),
        won: dealsByMonth[month]
          .filter((deal: Deal) => deal.stage === WON_STAGE)
          .reduce((acc: number, deal: Deal) => {
            acc += deal.amount;
            return acc;
          }, 0),
        pending: dealsByMonth[month]
          .filter(
            (deal: Deal) =>
              deal.stage !== WON_STAGE && deal.stage !== LOST_STAGE,
          )
          .reduce((acc: number, deal: Deal) => {
            const stageMultiplier =
              multiplier[deal.stage] ?? DEFAULT_PENDING_MULTIPLIER;
            acc += deal.amount * stageMultiplier;
            return acc;
          }, 0),
        lost: dealsByMonth[month]
          .filter((deal: Deal) => deal.stage === LOST_STAGE)
          .reduce((acc: number, deal: Deal) => {
            acc -= deal.amount;
            return acc;
          }, 0),
      };
    });

    return amountByMonth;
  }, [data]);

  if (error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetch()}
      />
    );
  }

  if (isPending) {
    return (
      <div
        aria-busy="true"
        role="status"
        className="flex flex-col"
        data-testid="deals-chart-skeleton"
      >
        <span className="sr-only">{translate("crm.common.loading")}</span>
        <Skeleton aria-hidden="true" className="h-[400px] w-full rounded-md" />
      </div>
    );
  }

  const range = months.reduce(
    (acc, month) => {
      acc.min = Math.min(acc.min, month.lost);
      acc.max = Math.max(acc.max, month.won + month.pending);
      return acc;
    },
    { min: 0, max: 0 },
  );
  return (
    <div className="flex flex-col">
      <div className="flex items-center mb-4">
        <div className="mr-3 flex">
          <TrendingUp className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.dashboard.deals_chart")}
        </h2>
      </div>
      <div className="h-[400px]">
        <ResponsiveBar
          data={months}
          indexBy="date"
          keys={["won", "pending", "lost"]}
          // design-system.md section 4: navy and navy tints only, no
          // semantic green/red and no gradient.
          colors={[NAVY, NAVY_70, NAVY_40]}
          margin={{ top: 30, right: 50, bottom: 30, left: 0 }}
          padding={0.3}
          valueScale={{
            type: "linear",
            min: range.min * 1.2,
            max: range.max * 1.2,
          }}
          indexScale={{ type: "band", round: true }}
          enableGridX={true}
          enableGridY={false}
          enableLabel={false}
          tooltip={({ value, indexValue }) => (
            <div className="p-2 bg-secondary rounded shadow inline-flex items-center gap-1 text-secondary-foreground">
              <strong>{indexValue}: </strong>&nbsp;
              <span className="tabular-nums">
                {value > 0 ? "+" : ""}
                {value.toLocaleString(APP_LOCALE, {
                  style: "currency",
                  currency,
                })}
              </span>
            </div>
          )}
          axisTop={{
            tickSize: 0,
            tickPadding: 12,
            style: {
              ticks: {
                text: tabularTickStyle("var(--color-muted-foreground)"),
              },
              legend: {
                text: tabularTickStyle("var(--color-muted-foreground)"),
              },
            },
          }}
          axisBottom={{
            legendPosition: "middle",
            legendOffset: 50,
            tickSize: 0,
            tickPadding: 12,
            style: {
              ticks: {
                text: tabularTickStyle("var(--color-muted-foreground)"),
              },
              legend: {
                text: tabularTickStyle("var(--color-muted-foreground)"),
              },
            },
          }}
          axisLeft={null}
          axisRight={{
            format: (v: any) => `${Math.abs(v / 1000)}k`,
            tickValues: 8,
            style: {
              ticks: {
                text: tabularTickStyle("var(--color-muted-foreground)"),
              },
              legend: {
                text: tabularTickStyle("var(--color-muted-foreground)"),
              },
            },
          }}
          markers={
            [
              {
                axis: "y",
                value: 0,
                lineStyle: { strokeOpacity: 0 },
                textStyle: tabularTickStyle(NAVY),
                legend: wonLabel,
                legendPosition: "top-left",
                legendOrientation: "vertical",
              },
              {
                axis: "y",
                value: 0,
                lineStyle: {
                  stroke: NAVY_40,
                  strokeWidth: 1,
                },
                textStyle: tabularTickStyle(NAVY_70),
                legend: lostLabel,
                legendPosition: "bottom-left",
                legendOrientation: "vertical",
              },
            ] as any
          }
        />
      </div>
    </div>
  );
});
